/**
 * Utility functions for consistent formatting of dates and currency amounts
 * Based on the formatting logic from facility_compliance_demo_v2.html
 */
import { getSelectedCountryCode } from "@utils/countryUtils";

/**
 * Parse a strict ISO date-only string (YYYY-MM-DD) as a local calendar date.
 * `new Date("2026-04-01")` is UTC midnight and shows as Mar 31 in US time zones;
 * compliance APIs use date-only strings for calendar deadlines — match that semantics here.
 *
 * @returns A valid Date in local TZ, or null if the string is not exactly YYYY-MM-DD or is invalid.
 */
export function parseIsoDateOnlyAsLocalDate(dateString: string): Date | null {
  const trimmed = dateString.trim();
  const m = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  const dt = new Date(y, mo - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return null;
  }
  return dt;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Profile date fields use canonical YYYY-MM-DD for the API. This accepts pasted or
 * typed US-style dates (m/d/yyyy, mm/dd/yyyy), ISO date-only, yyyy/m/d, and
 * ISO timestamps (first 10 chars), and returns YYYY-MM-DD or null if not parseable.
 */
export function normalizeProfileDateInputToIso(raw: string): string | null {
  let t = String(raw ?? "").trim();
  if (!t) return null;
  if (
    (t.startsWith('"') && t.endsWith('"')) ||
    (t.startsWith("'") && t.endsWith("'"))
  ) {
    t = t.slice(1, -1).trim();
  }
  const firstLine = (t.split(/\r?\n/)[0] ?? "").trim();
  t = firstLine || t;

  const isoLead = t.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s]|$)/);
  if (isoLead && parseIsoDateOnlyAsLocalDate(isoLead[1])) {
    return isoLead[1];
  }

  const us = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (us) {
    const mo = parseInt(us[1], 10);
    const d = parseInt(us[2], 10);
    let y = parseInt(us[3], 10);
    if (us[3].length === 2) {
      y = y < 70 ? 2000 + y : 1900 + y;
    }
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return null;
    const check = new Date(y, mo - 1, d);
    if (
      check.getFullYear() !== y ||
      check.getMonth() !== mo - 1 ||
      check.getDate() !== d
    ) {
      return null;
    }
    return `${y}-${pad2(mo)}-${pad2(d)}`;
  }

  const ymd = t.match(/^(\d{4})[/.-](\d{1,2})[/.-](\d{1,2})$/);
  if (ymd) {
    const y = parseInt(ymd[1], 10);
    const mo = parseInt(ymd[2], 10);
    const d = parseInt(ymd[3], 10);
    if (mo < 1 || mo > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) return null;
    const check = new Date(y, mo - 1, d);
    if (
      check.getFullYear() !== y ||
      check.getMonth() !== mo - 1 ||
      check.getDate() !== d
    ) {
      return null;
    }
    return `${y}-${pad2(mo)}-${pad2(d)}`;
  }

  return null;
}

/** While typing, keep digits only and format as mm/dd/yyyy (partial allowed). */
export function formatUsDateDraftInput(raw: string): string {
  const digits = String(raw ?? "").replace(/\D/g, "").slice(0, 8);
  const mm = digits.slice(0, 2);
  const dd = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  if (digits.length <= 2) return mm;
  if (digits.length <= 4) return `${mm}/${dd}`;
  return `${mm}/${dd}/${yyyy}`;
}

/** ISO date-only (or ISO-prefixed string) shown as mm/dd/yyyy for profile text inputs. */
export function isoDateOnlyToUsSlash(iso: string): string {
  const trimmed = String(iso ?? "").trim();
  const head = trimmed.match(/^(\d{4}-\d{2}-\d{2})/)?.[1];
  if (!head) return trimmed;
  const d = parseIsoDateOnlyAsLocalDate(head);
  if (!d) return trimmed;
  return `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`;
}

/**
 * Format date strings according to country-specific formats
 * @param dateString - The date string to format
 * @param country - The country code for locale-specific formatting (default: 'USA')
 * @returns Formatted date string
 */
export function formatDate(
  dateString: string | null | undefined,
  country: string = "USA"
): string {
  if (!dateString) return "";

  const localDay = parseIsoDateOnlyAsLocalDate(dateString);
  const date = localDay ?? new Date(dateString);
  if (isNaN(date.getTime())) return dateString; // Return original if invalid date

  switch (country.toUpperCase()) {
    case "USA":
    case "US":
      // Month Day, Year format (e.g., "March 15, 2024")
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    case "UK":
    case "GB":
      // D Month Year format (e.g., "15 March 2024")
      return date.toLocaleDateString("en-GB", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    case "CA":
    case "CANADA":
      // Month D, Y format (e.g., "March 15, 2024")
      return date.toLocaleDateString("en-CA", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    case "DE":
    case "GERMANY":
      // D. Month Y format (e.g., "15. März 2024")
      return date.toLocaleDateString("de-DE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    default:
      // Month Day, Year format as fallback
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
  }
}

const DATE_TIME_DISPLAY_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
};

/**
 * Format ISO date-time strings for display (e.g. history timestamps).
 * Uses the same country/locale mapping as {@link formatDate}.
 */
export function formatDateTime(
  dateTimeString: string | null | undefined,
  country: string = "USA"
): string {
  if (!dateTimeString) return "";
  const date = new Date(dateTimeString);
  if (isNaN(date.getTime())) return String(dateTimeString);

  switch (country.toUpperCase()) {
    case "USA":
    case "US":
      return date.toLocaleString("en-US", DATE_TIME_DISPLAY_OPTS);
    case "UK":
    case "GB":
      return date.toLocaleString("en-GB", DATE_TIME_DISPLAY_OPTS);
    case "CA":
    case "CANADA":
      return date.toLocaleString("en-CA", DATE_TIME_DISPLAY_OPTS);
    case "DE":
    case "GERMANY":
      return date.toLocaleString("de-DE", DATE_TIME_DISPLAY_OPTS);
    default:
      return date.toLocaleString("en-US", DATE_TIME_DISPLAY_OPTS);
  }
}

/**
 * Format currency amounts with proper locale-specific formatting
 * @param amount - The amount to format (number or string)
 * @param country - The country code for locale-specific formatting (default: 'USA')
 * @returns Formatted currency string
 */

/** Intl options: default shows cents; whole dollars omit .00 */
function _currencyIntlOptions(wholeDollars: boolean): Pick<
  Intl.NumberFormatOptions,
  "minimumFractionDigits" | "maximumFractionDigits"
> {
  return wholeDollars
    ? { minimumFractionDigits: 0, maximumFractionDigits: 0 }
    : {};
}

export function formatCurrencyAmount(
  amount: number | string | null | undefined,
  country: string = "USA",
  currencyCodeOverride?: string,
  wholeDollars: boolean = false
): string {
  if (amount === null || amount === undefined || amount === "") return "";

  const numAmount = parseFloat(String(amount));
  if (isNaN(numAmount)) return String(amount); // Return original if not a number

  const frac = _currencyIntlOptions(wholeDollars);

  // If a specific currency code is provided (e.g., from countries API), use it directly
  if (currencyCodeOverride) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCodeOverride,
        ...frac,
      }).format(numAmount);
    } catch (_) {}
  }

  // Auto-detect selected country code (no cached countries needed)
  try {
    const selected = getSelectedCountryCode();
    if (selected) {
      country = selected;
    }
  } catch (_) {}

  switch (country.toUpperCase()) {
    case "USA":
    case "US":
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        ...frac,
      }).format(numAmount);
    case "ARE":
    case "AE":
    case "UAE":
      return new Intl.NumberFormat("en-AE", {
        style: "currency",
        currency: "AED",
        currencyDisplay: "code", // show AED instead of Arabic symbol
        ...frac,
      }).format(numAmount);
    case "UK":
    case "GB":
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "GBP",
        ...frac,
      }).format(numAmount);
    case "CA":
    case "CANADA":
      return new Intl.NumberFormat("en-CA", {
        style: "currency",
        currency: "CAD",
        ...frac,
      }).format(numAmount);
    case "DE":
    case "GERMANY":
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        ...frac,
      }).format(numAmount);
    case "IN":
    case "IND":
    case "INDIA":
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        ...frac,
      }).format(numAmount);
    default:
      // Default USD format
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        ...frac,
      }).format(numAmount);
  }
}

/**
 * Same as formatCurrencyAmount but without fractional cents (.00) when the amount is whole dollars.
 */
export function formatCurrencyAmountWhole(
  amount: number | string | null | undefined,
  country: string = "USA",
  currencyCodeOverride?: string
): string {
  return formatCurrencyAmount(amount, country, currencyCodeOverride, true);
}

/**
 * Format license fee with support for estimates, null amounts, worked examples, and fee basis
 * @param fee - The fee object containing amount, isEstimate, notes, etc.
 * @param license - Optional full license object containing workedExample, feeBasis, renewalCycle
 * @param country - The country code for locale-specific formatting
 * @returns Object with formatted display text, worked example text, and styling flags
 */
export function formatLicenseFee(
  fee: {
    amount?: number | null;
    currency?: string;
    isEstimate?: boolean;
    notes?: string;
    formula?: string;
  } | null | undefined,
  license?: {
    feeBasis?: string | null;
    renewalCycle?: string | null;
    workedExample?: {
      inputs?: Record<string, number | string | null>;
      result?: {
        amount?: number | null;
        currency?: string;
      };
    } | null;
  } | null,
  country: string = "USA"
): { 
  displayText: string; 
  workedExampleText: string | null;
  isEstimate: boolean; 
  isUnknown: boolean; 
  notes: string;
} {
  // Handle null/undefined fee object
  if (!fee) {
    return { displayText: "N/A", workedExampleText: null, isEstimate: false, isUnknown: true, notes: "" };
  }

  const notes = fee.notes || "";
  const isEstimate = fee.isEstimate || false;

  // Handle null/undefined amount (unknown fee)
  if (fee.amount === null || fee.amount === undefined) {
    const displayText = notes || fee.formula || "Fee varies - contact agency";
    return { displayText, workedExampleText: null, isEstimate: true, isUnknown: true, notes };
  }

  // Format the actual amount
  const formattedAmount = formatCurrencyAmount(fee.amount, country, fee.currency);
  
  // Build display text with fee basis and renewal cycle
  let displayText = isEstimate ? `~${formattedAmount}` : formattedAmount;
  
  if (license?.feeBasis) {
    // Clean up feeBasis - remove "per " prefix if present
    const basis = license.feeBasis.replace(/^per\s+/i, '');
    displayText += ` /${basis}`;
  }
  
  if (license?.renewalCycle && license.renewalCycle !== 'one-time') {
    displayText += ` /${license.renewalCycle}`;
  }
  
  // Build worked example text if available
  let workedExampleText: string | null = null;
  if (license?.workedExample?.result?.amount !== null && license?.workedExample?.result?.amount !== undefined) {
    const exampleAmount = formatCurrencyAmount(
      license.workedExample.result.amount, 
      country, 
      license.workedExample.result.currency
    );
    
    const inputs = license.workedExample.inputs;
    if (inputs) {
      const inputParts = Object.entries(inputs)
        .filter(([_, v]) => v !== null && v !== undefined)
        .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`);
      const inputDesc = inputParts.join(', ');
      if (inputDesc) {
        workedExampleText = `For ${inputDesc}: ${exampleAmount}`;
      } else {
        workedExampleText = `Example: ${exampleAmount}`;
      }
    } else {
      workedExampleText = `Example: ${exampleAmount}`;
    }
  }
  
  return { displayText, workedExampleText, isEstimate, isUnknown: false, notes };
}

/**
 * Enhanced currency formatter that handles various text patterns
 * Formats currency values with commas and proper symbols
 * @param text - Text containing currency amounts
 * @returns Formatted text with proper currency formatting
 */
export function formatCurrency(
  text: string | number | null | undefined,
  isSymbol: boolean = true
): string {
  if (!text) return String(text || "");

  // Convert to string if it's not already
  let result = typeof text === "string" ? text : String(text);

  // Handle dollar amounts with $ prefix (e.g., $20000 -> $20,000)
  result = result.replace(/\$(\d+(?:,\d{3})*|\d+)/g, function (match, amount) {
    const number = parseInt(amount.replace(/,/g, ""));
    const formatted = number.toLocaleString();
    return isSymbol ? "$" + formatted : formatted;
  });

  // Handle USD amounts without $ prefix (e.g., 20000 USD -> $20,000)
  result = result.replace(
    /(\d+(?:,\d{3})*|\d+)\s*USD/gi,
    function (match, amount) {
      const number = parseInt(amount.replace(/,/g, ""));
      const formatted = number.toLocaleString();
      return isSymbol ? "$" + formatted : formatted;
    }
  );

  // Handle range amounts with USD (e.g., 15000-20000 USD -> $15,000-$20,000)
  result = result.replace(
    /(\d+(?:,\d{3})*|\d+)\s*-\s*(\d+(?:,\d{3})*|\d+)\s*USD/gi,
    function (match, amount1, amount2) {
      const number1 = parseInt(amount1.replace(/,/g, ""));
      const number2 = parseInt(amount2.replace(/,/g, ""));
      const left = number1.toLocaleString();
      const right = number2.toLocaleString();
      return isSymbol ? "$" + left + "-$" + right : left + "-" + right;
    }
  );

  // Handle INR prefix (e.g., ₹100000 -> ₹1,00,000)
  result = result.replace(/₹(\d+(?:,\d{3})*|\d+)/g, function (match, amount) {
    const n = parseInt(amount.replace(/,/g, ""));
    const formatted = n.toLocaleString("en-IN");
    return isSymbol ? "₹" + formatted : formatted;
  });

  // Handle INR suffix (e.g., 100000 INR -> ₹1,00,000)
  result = result.replace(/(\d+(?:,\d{3})*|\d+)\s*INR/gi, function (match, amount) {
    const n = parseInt(amount.replace(/,/g, ""));
    const formatted = n.toLocaleString("en-IN");
    return isSymbol ? "₹" + formatted : formatted;
  });

  // Handle INR ranges (e.g., 50000-100000 INR)
  result = result.replace(
    /(\d+(?:,\d{3})*|\d+)\s*-\s*(\d+(?:,\d{3})*|\d+)\s*INR/gi,
    function (match, a1, a2) {
      const n1 = parseInt(a1.replace(/,/g, ""));
      const n2 = parseInt(a2.replace(/,/g, ""));
      const left = n1.toLocaleString("en-IN");
      const right = n2.toLocaleString("en-IN");
      return isSymbol ? "₹" + left + "-₹" + right : left + "-" + right;
    }
  );

  // Handle AED prefix (e.g., AED 10000 -> AED 10,000)
  result = result.replace(/AED\s*(\d+(?:,\d{3})*|\d+)/gi, function (match, amount) {
    const n = parseInt(amount.replace(/,/g, ""));
    const formatted = n.toLocaleString("en-AE");
    return isSymbol ? "AED " + formatted : formatted;
  });

  // Handle AED suffix (e.g., 10000 AED -> AED 10,000)
  result = result.replace(/(\d+(?:,\d{3})*|\d+)\s*AED/gi, function (match, amount) {
    const n = parseInt(amount.replace(/,/g, ""));
    const formatted = n.toLocaleString("en-AE");
    return isSymbol ? "AED " + formatted : formatted;
  });

  // Handle AED ranges (e.g., 5000-10000 AED)
  result = result.replace(
    /(\d+(?:,\d{3})*|\d+)\s*-\s*(\d+(?:,\d{3})*|\d+)\s*AED/gi,
    function (match, a1, a2) {
      const n1 = parseInt(a1.replace(/,/g, ""));
      const n2 = parseInt(a2.replace(/,/g, ""));
      const left = n1.toLocaleString("en-AE");
      const right = n2.toLocaleString("en-AE");
      return isSymbol ? "AED " + left + "-AED " + right : left + "-" + right;
    }
  );

  // Handle plain number ranges without currency (e.g., 5000-20000 -> $5,000-$20,000 or 5,000-20,000)
  // But exclude regulatory codes like WAC, CFR, USC, etc.
  result = result.replace(
    /\b(\d+)\s*-\s*(\d+)\b/g,
    function (match, amount1, amount2, offset, string) {
      // Check if this is part of a regulatory code (look for context before the match)
      const beforeMatch = string.substring(Math.max(0, offset - 20), offset);
      const afterMatch = string.substring(
        offset + match.length,
        offset + match.length + 20
      );

      // Skip if it's a regulatory code (WAC, CFR, USC, etc.)
      if (
        /\b(WAC|CFR|USC|RCW|ORS|CRS)\s*$/i.test(beforeMatch) ||
        /^\s*-\s*\d+/i.test(afterMatch)
      ) {
        return match; // Don't format regulatory codes
      }

      // Skip if it's a date pattern (e.g., 1/30/2026, 12/30/2025)
      if (/\/\d+\/\d+/.test(beforeMatch) || /\d+\/\d+\//.test(afterMatch)) {
        return match; // Don't format dates
      }

      const number1 = parseInt(amount1);
      const number2 = parseInt(amount2);

      // Only format as currency if numbers are reasonably large (likely currency amounts)
      if (number1 >= 100 && number2 >= 100) {
        const left = number1.toLocaleString();
        const right = number2.toLocaleString();
        return isSymbol ? "$" + left + "-$" + right : left + "-" + right;
      }

      return match; // Don't format small numbers that might not be currency
    }
  );

  return result;
}

const EXCEL_NAME_BUSINESS_SUFFIX = /^(llc|llp|lp|inc|pllc|dba)$/i;

/**
 * Title-case each word: lowercase with an initial capital. Numeric tokens are unchanged.
 * Two-letter alphabetic tokens (e.g. state abbreviations) stay uppercase.
 */
export function formatTitleCaseWords(raw: string | undefined | null): string {
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";

  const capitalizeSegment = (segment: string): string => {
    if (!segment) return segment;
    const lower = segment.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  return trimmed
    .split(/\s+/)
    .map((word) => {
      if (/^\d+$/.test(word)) return word;
      if (/^[A-Z]{2}$/.test(word)) return word;
      return word
        .split("-")
        .map((seg) => capitalizeSegment(seg))
        .join("-");
    })
    .join(" ");
}

/**
 * Normalize a name parsed from Excel: rows are often ALL CAPS; convert to mixed case
 * so "DIANE KIRBIS" → "Diane Kirbis" and "ARLETA MEDICAL SUPPLY" → "Arleta Medical Supply".
 * Hyphenated parts are capitalized per segment ("VARMA-KULKARNI" → "Varma-Kulkarni").
 * Trailing business suffix tokens stay uppercase (LLC, INC, LP, …).
 */
export function formatExcelExtractedName(raw: string | undefined | null): string {
  if (raw == null) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";

  const capitalizeSegment = (segment: string): string => {
    if (!segment) return segment;
    const lower = segment.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const words = trimmed.split(/\s+/).map((word) =>
    word
      .split("-")
      .map((seg) => capitalizeSegment(seg))
      .join("-"),
  );

  if (words.length > 0) {
    const last = words[words.length - 1];
    if (EXCEL_NAME_BUSINESS_SUFFIX.test(last)) {
      words[words.length - 1] = last.toUpperCase();
    }
  }

  return words.join(" ");
}
